import { apiClient } from './client'
import type { CursorPage } from './accounts'
import type {
  BulkApplyAsyncView,
  BulkEditableFieldView,
  BulkOperationItemView,
  BulkOperationView,
  CreateBulkOperationRequest,
} from '../types/bulk'

/**
 * Typed fetch wrappers for `module.bulk`'s `BulkOperationController` — same thin-wrapper
 * convention as `api/jobs.ts`/`api/notifications.ts`. Backs `screens/bulk/BulkOperationsScreen.tsx`.
 *
 * `listBulkOperations`/`listBulkEditableFields` are THIN ADDITIVE GETs this story adds (no design/
 * api-spec entry named a list-all-operations or list-editable-fields route before now) — every
 * other function here rides an already-published route (`create`/`get`/`items`/`approve`/`apply`).
 */

export interface ListBulkOperationsParams {
  kind?: string | null
  status?: string | null
  targetEntity?: string | null
  cursor?: string | null
  limit?: number
}

function toQuery(params: ListBulkOperationsParams): string {
  const q = new URLSearchParams()
  if (params.kind) q.set('kind', params.kind)
  if (params.status) q.set('status', params.status)
  if (params.targetEntity) q.set('target_entity', params.targetEntity)
  if (params.cursor) q.set('cursor', params.cursor)
  q.set('limit', String(params.limit ?? 50))
  return q.toString()
}

/** `GET /bulk-operations` (thin additive GET, this story) — past and in-progress operations for
 * the caller's tenant, newest first. */
export async function listBulkOperations(
  params: ListBulkOperationsParams = {},
  signal?: AbortSignal,
): Promise<CursorPage<BulkOperationView>> {
  const { data } = await apiClient.get<CursorPage<BulkOperationView>>(
    `/bulk-operations?${toQuery(params)}`,
    { signal },
  )
  return data
}

/** `GET /bulk-operations/{id}` — one operation's summary + counts (already-published). */
export async function getBulkOperation(id: string, signal?: AbortSignal): Promise<BulkOperationView> {
  const { data } = await apiClient.get<BulkOperationView>(`/bulk-operations/${id}`, { signal })
  return data
}

/** `GET /bulk-operations/{id}/items` — the per-row preview/outcome diff (already-published). */
export async function listBulkOperationItems(
  id: string,
  signal?: AbortSignal,
): Promise<BulkOperationItemView[]> {
  const { data } = await apiClient.get<BulkOperationItemView[]>(`/bulk-operations/${id}/items`, { signal })
  return data
}

/** `GET /bulk-editable-fields?entity=...` (thin additive GET, this story) — the field-picker
 * allow-list for one `target_entity`. */
export async function listBulkEditableFields(
  entity: string,
  signal?: AbortSignal,
): Promise<BulkEditableFieldView[]> {
  const { data } = await apiClient.get<BulkEditableFieldView[]>(
    `/bulk-editable-fields?entity=${encodeURIComponent(entity)}`,
    { signal },
  )
  return data
}

/** `POST /bulk-operations` — creates the operation AND resolves its preview in one call
 * (already-published: the response body already carries the resolved preview counts). */
export async function createBulkOperation(request: CreateBulkOperationRequest): Promise<BulkOperationView> {
  const { data } = await apiClient.post<BulkOperationView>('/bulk-operations', request)
  return data
}

/** `POST /bulk-operations/{id}/approve` — the `preview -> awaiting_approval` transition
 * (already-published). Distinct from apply; never collapsed (design §4, AC6). */
export async function approveBulkOperation(id: string): Promise<BulkOperationView> {
  const { data } = await apiClient.post<BulkOperationView>(`/bulk-operations/${id}/approve`, {})
  return data
}

/** `POST /bulk-operations/{id}/apply` (already-published) — `reason` is the SINGLE mandatory
 * batch comment threaded onto every constituent guarded row's `AdjustmentService` call
 * (`BulkAdjustmentRouter`); required whenever the operation touches a `requires_reason=true`
 * field (server re-validates regardless of what the client sends). Returns either the terminal
 * `BulkOperationView` (sync path) or a `{jobId}` async handle (AC24, batch over threshold) —
 * `isBulkApplyAsync` (types/bulk.ts) distinguishes the two. */
export async function applyBulkOperation(
  id: string,
  reason?: string,
): Promise<BulkOperationView | BulkApplyAsyncView> {
  const { data } = await apiClient.post<BulkOperationView | BulkApplyAsyncView>(
    `/bulk-operations/${id}/apply`,
    reason ? { reason } : {},
  )
  return data
}
